/**
 * PartnerToAgentUpgradeButton — controlled Partner→Agent conversion CTA.
 *
 * Rules enforced:
 *  - A user cannot self-invite into Agent. The Partner→Agent path is the
 *    only supported way for a Partner to become an Agent.
 *  - Confirmation modal explains the conversion before it happens.
 *  - On confirm, calls the convert_partner_to_agent RPC which:
 *      • archives the Partner profile (status=converted)
 *      • creates/updates the Agent profile as pending
 *      • preserves Connector / General Agent roles
 *      • preserves historical Partner earnings & invite relationships
 */
import React from 'react';
import { ConfirmDialog } from '@bright-web-skeleton/ui';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface Props {
  /** Render prop for the trigger button. Receives an `onClick` to open the modal. */
  children: (open: () => void) => React.ReactNode;
}

export const PartnerToAgentUpgradeButton: React.FC<Props> = ({ children }) => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [acknowledged, setAcknowledged] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleConfirm = async () => {
    if (!acknowledged) {
      toast.error('Please acknowledge the terms before sending the request.');
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('convert_partner_to_agent');
      const result = (data ?? null) as { ok?: boolean; error?: string } | null;
      if (error || !result?.ok) {
        const reason = result?.error || error?.message || 'unknown_error';
        if (reason === 'already_active_agent') {
          toast.error('You already have an active Agent role.');
        } else if (reason === 'no_active_partner') {
          toast.error('No active Partner role found to convert.');
        } else {
          toast.error('Conversion failed. Please try again.');
        }
        return;
      }
      toast.success('Agent access requested. Pending admin approval.');
      setOpen(false);
      navigate('/agent/dashboard');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {children(() => { setAcknowledged(false); setOpen(true); })}
      <ConfirmDialog
        open={open}
        onOpenChange={(o) => { if (!loading) setOpen(o); }}
        title="Request Agent Access"
        confirmLabel="Send request"
        cancelLabel="Cancel"
        isLoading={loading}
        onConfirm={handleConfirm}
      >
        <div className="space-y-4 text-sm text-muted-foreground">
          <p>
            Your request will be sent for admin approval. Your Agent role becomes
            active only after approval and applies to future orders only.
          </p>
          <p className="text-xs">
            Partner and Agent cannot be active at the same time. Your Connector and
            General Agent roles (if any) remain active. Historical Partner earnings
            and invites are preserved exactly as they are — past orders are not
            recalculated.
          </p>
          <label className="flex items-start gap-2 cursor-pointer text-foreground">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              disabled={loading}
            />
            <span>I understand my Agent role becomes active only after admin approval.</span>
          </label>
        </div>
      </ConfirmDialog>
    </>
  );
};

export default PartnerToAgentUpgradeButton;